import { Dictionary } from 'lodash';
import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { Project, ProjectUserLite } from 'api/project';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { GroupBy } from 'components/organisms/resource-management/types';
import {
  resourceIdForProjectAsLeaf,
  resourceIdForUserAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { InvitedMembers } from 'data/members/slice';
import { getProjectUsersLiteKey } from 'queries/common';
import { ALL_PROJECTS_QUERY_KEY } from 'queries/constants';
import { logger } from 'services';
import { useMultiProjectWsUpdatesInRM } from './multi-project-ws-manager';
import { ProjectMembersWsProps } from './project-members-socket-manager';

export const useProjectMembersUpdateWSManager = (
  props: ProjectMembersWsProps,
): void => {
  const queryClient = useQueryClient();
  const { schedulerRef, groupBy, users, projectLookup, projectChannels } =
    props;

  const handleMembersUpdate = useCallback(
    (socketData: SocketData<InvitedMembers[]>) => {
      logger.log('project-members-update ~ socketData', socketData);
      try {
        const { data, projectId } = socketData;

        if (data.length !== 2) {
          return;
        }
        const specificProject = projectLookup[projectId];

        if (specificProject == null) {
          return;
        }
        const { userId: projectOwnerId } = specificProject.projectOwner;

        const hasProjectOwner = data.find(
          ({ user: { userId } }) => projectOwnerId === userId,
        );

        if (hasProjectOwner) {
          const otherUser = data.find(
            ({ user: { userId } }) => projectOwnerId !== userId,
          );
          const newProjectOwner = users.find(
            (user) => user.userId === otherUser?.user.userId,
          );

          if (newProjectOwner == null) {
            return;
          }
          queryClient.setQueryData<Project[]>(
            ALL_PROJECTS_QUERY_KEY,
            (previous) =>
              previous?.map((project) => {
                if (project.projectId === projectId) {
                  return {
                    ...project,
                    projectOwner: newProjectOwner,
                  };
                }

                return project;
              }) ?? [],
          );
          const projectUsers =
            queryClient.getQueryData<Dictionary<ProjectUserLite[]>>(
              getProjectUsersLiteKey(),
            )?.[projectId] ?? [];
          const oldProjectOwnerVersions = projectUsers.filter(
            ({ user: { userId } }) => userId === projectOwnerId,
          );
          const newProjectOwnerVersions = projectUsers.filter(
            ({ user: { userId } }) => userId === newProjectOwner.userId,
          );

          if (groupBy === GroupBy.Users) {
            oldProjectOwnerVersions.forEach(
              ({ role, referenceTeam, user: { userId } }) => {
                const resource =
                  schedulerRef.current.instance.resourceStore.getById(
                    resourceIdForProjectAsLeaf({
                      projectId,
                      userId,
                      roleId: role?.roleId,
                      referenceTeamId: referenceTeam?.teamId,
                    }),
                  );

                resource?.set('isUserProjectOwner', false);
              },
            );
            newProjectOwnerVersions.forEach(
              ({ role, referenceTeam, user: { userId } }) => {
                const resource =
                  schedulerRef.current.instance.resourceStore.getById(
                    resourceIdForProjectAsLeaf({
                      projectId,
                      userId,
                      roleId: role?.roleId,
                      referenceTeamId: referenceTeam?.teamId,
                    }),
                  );

                resource?.set('isUserProjectOwner', true);
              },
            );
          } else {
            oldProjectOwnerVersions.forEach(
              ({ role, referenceTeam, user: { userId } }) => {
                const oldUserResource =
                  schedulerRef.current.instance.resourceStore.getById(
                    resourceIdForUserAsLeaf({
                      projectId,
                      userId,
                      roleId: role?.roleId,
                      referenceTeamId: referenceTeam?.teamId,
                    }),
                  );

                oldUserResource?.set('isProjectOwner', false);
              },
            );
            newProjectOwnerVersions.forEach(
              ({ role, referenceTeam, user: { userId } }) => {
                const oldUserResource =
                  schedulerRef.current.instance.resourceStore.getById(
                    resourceIdForUserAsLeaf({
                      projectId,
                      userId,
                      roleId: role?.roleId,
                      referenceTeamId: referenceTeam?.teamId,
                    }),
                  );

                oldUserResource?.set('isProjectOwner', true);
              },
            );
          }
        }
      } catch (error) {
        logger.error('Error while updating project members', { error });
      }
    },
    [groupBy, projectLookup, queryClient, schedulerRef, users],
  );

  useMultiProjectWsUpdatesInRM(
    projectChannels,
    handleMembersUpdate,
    EventType(Action.Update, ObjectType.ProjectMembers),
  );
};
